import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { ICalendar, IBizDays } from '@/appLogic/interfaces/date';
import { getWorkDays } from '@/appLogic/business/inventorySimulation';
import { simpleCopy } from '@/utils/deepCopy';
import { DAY_STD } from '@/coreDomainsModule/constants';
import { getBusinessManager } from '@/businessRepository/organizationTable/businessManage';
import { getPreference, putPreference } from '@/appLogic/preferenceManage';
import { getOrganizationId } from '@/utils/token';
import { DateService } from '@/coreDomainsModule/services/dateService';
const MAX_CALCULATE_MONTHS = 12; // 今月を含めて計算

interface CheckCalendar {
  state: boolean;
  checkDate: YearMonth;
  fails: YearMonth[]; // 1 ~ 12 月が
}

interface YearMonth {
  year: number;
  month: number;
}

interface CheckResult {
  state: boolean;
  fails: YearMonth[]; // 1 ~ 12 月が
}

/**
 * カレンダー情報の取得
 */
const getCalendar = async (): Promise<ICalendar> => {
  const calendar: any = await getPreference(await getOrganizationId(), 'calendar');
  if (!calendar) {
    return {
      organizationId: await getOrganizationId(),
      updatedAt: Math.floor(new Date().getTime() / 1000),
      workDays: [] as IBizDays[],
      unRegisterCalendarAlert: {
        updateAt: Math.floor(new Date().getTime() / 1000),
        isDisplay: true,
      },
    };
  }
  calendar.workDays.sort(DateService.compareYearMonths);
  return calendar as ICalendar;
};

const getCheckCalendar = function (): CheckCalendar | undefined {
  try {
    const get = localStorage.getItem('checkCalendar');
    if (!get) {
      return undefined; // 基本的にはundefinedで統一したい
    }
    const checkCalendar: CheckCalendar = JSON.parse(get);
    return checkCalendar;
  } catch (err) {
    console.error(err);
    return undefined;
  }
};

const setCheckCalendar = function (checkCalendar: CheckCalendar): Error | undefined {
  try {
    const set = JSON.stringify(checkCalendar);
    localStorage.setItem('checkCalendar', set);
    return undefined;
  } catch (err) {
    console.error('setCheckCalendar', err);
    return err as Error;
  }
};

/**
 *
 * @param businesses
 * @param date
 */
const calculateMaxMonths = function (businesses: IBusiness[], date?: Date): number {
  const now = date || new Date();
  const dayStd = DAY_STD; // getTimeにおける1日の数値
  const nowTime = now.getTime();
  let maxMonths = 1; // 当月あわせたmin(n,1)
  for (let i = 0; i < businesses.length; i++) {
    const leadTime = businesses[i].leadTime;
    let sum = 1; // diffに当月,当年は0なので
    if (leadTime) {
      // leadTimeは日単位前提
      const targetDate = new Date(nowTime + leadTime * dayStd);
      sum += (targetDate.getFullYear() - now.getFullYear()) * 12; //一年は12ヶ月なので
      sum += targetDate.getMonth() - now.getMonth();
    }
    if (sum > maxMonths) {
      maxMonths = sum;
    }
  }
  return maxMonths;
};

const calculateCalendar = async function (year: number, month: number, maxMonths: number): Promise<CheckResult> {
  console.log('calculateCalendar');
  const calendar = await getCalendar();
  const workDays = getWorkDays(calendar, year, month);
  const fails: YearMonth[] = [];
  for (let i = 0; i < maxMonths; i++) {
    const targetMonth: number = Math.floor((month + i) % 12); // 0スタート
    const targetYear: number = year + Math.floor((month + i) / 12);
    const d: number[] = getWorkDays(calendar, targetYear, targetMonth + 1) || [];
    if (d.length == 0) {
      fails.push({
        year: targetYear,
        month: targetMonth + 1,
      });
    }
  }
  if (fails.length > 0) {
    return { state: false, fails: fails };
  } else {
    return { state: true, fails: [] };
  }
};

/**
 *
 */
const removeChecker = function () {
  // 他で利用される関数であることを考えて,"Calendar"という名称がどうかの議論の余地があるので
  try {
    localStorage.removeItem('checkCalendar');
  } catch (err) {
    console.error(err);
    return;
  }
};
const allWorkingDays = function (year: number, month: number): number[] {
  const maxDates = new Date(year, month, 0).getDate(); // 次の月の0日目を計算する -> 当月のmaxDate
  const output: number[] = [];
  for (let i = 1; i <= maxDates; i++) {
    output.push(i);
  }
  return output;
};
const calculateValue = function (yearMonth: YearMonth): number {
  return yearMonth.year * 1000 + yearMonth.month;
};

/**
 *
 * @param fails
 */
const autoRegister = async function (fails: YearMonth[]): Promise<Error | undefined> {
  /*
  引数にcalendarを追加して、独立性高めるのか内包するのかに関しては、
  autoRegisterだけでカレンダーへの追加(putCalendar)を行いたいので、putCalendarを行うまえのデータは同一であって欲しいので、
  calendarは読み込み
  */
  console.log('autoRegister');
  try {
    const calendar = await getCalendar();
    const output: IBizDays[] = [];
    const sortedFails = fails.sort((a, b) => {
      if (a.year === b.year) {
        return a.month - b.month;
      } else {
        return a.year - b.year;
      }
    });
    let failsIndex = 0;
    for (let i = 0; i < calendar.workDays.length; i++) {
      // ここ、workDaysが肥大かすることを考えるとアルゴリズムを変えるタイミングが来る可能性あり。
      // その前に仕様も変わる可能性あるので現状制約が小さいことを利用し、全探索してpush
      if (
        sortedFails[failsIndex] &&
        calculateValue(sortedFails[failsIndex]) <= calculateValue({ year: calendar.workDays[i].year, month: calendar.workDays[i].month })
      ) {
        output.push({
          month: sortedFails[failsIndex].month,
          year: sortedFails[failsIndex].year,
          days: allWorkingDays(sortedFails[failsIndex].year, sortedFails[failsIndex].month),
        });
        failsIndex++;
      } else {
        output.push(simpleCopy(calendar.workDays[i]));
      }
    }
    calendar.workDays = output;
    calendar.workDays.sort(DateService.compareYearMonths);
    await putPreference(await getOrganizationId(), 'calendar', calendar);
    removeChecker();
  } catch (err) {
    console.error(err);
    return err as Error;
  }
};

/**
 *
 * @param targetBusinesses
 * @param date
 */
const checkState = async function (
  targetBusinesses?: IBusiness[] /*　ここ必須か任意か微妙... dashboardLayoutの関係で任意になってる */,
  date?: Date
): Promise<CheckResult> {
  let now = date;
  if (now === undefined) {
    now = new Date() as Date;
  }
  const businesses = targetBusinesses || (await getBusinessManager().getAll());
  const nowYearMonth: YearMonth = {
    year: now.getFullYear(),
    month: now.getMonth(), // 0 ~ 11
  };
  const localCheckCalendar: CheckCalendar | undefined = getCheckCalendar();
  if (
    localCheckCalendar &&
    localCheckCalendar?.checkDate.year === nowYearMonth.year &&
    localCheckCalendar?.checkDate.year === nowYearMonth.month
  ) {
    return { state: localCheckCalendar.state, fails: localCheckCalendar.fails };
  } else {
    // localStorageの更新
    const targetCheckCalendar: CheckCalendar = {
      state: false,
      checkDate: nowYearMonth,
      fails: [],
    };
    return await calculateCalendar(
      targetCheckCalendar.checkDate.year,
      targetCheckCalendar.checkDate.month,
      calculateMaxMonths(businesses, now)
    ).then((result) => {
      targetCheckCalendar.state = result.state;
      targetCheckCalendar.fails = result.fails;
      setCheckCalendar(targetCheckCalendar);
      return result;
    });
  }
};

export { getCalendar, checkState, removeChecker, autoRegister, calculateMaxMonths };
export { CheckResult };
